import React from 'react';
import { BusinessInfo } from '../types/preview';

interface BusinessInfoSidebarProps {
  businessInfo: BusinessInfo;
}

const BusinessInfoSidebar: React.FC<BusinessInfoSidebarProps> = ({ businessInfo }) => {
  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 80) return 'text-accent bg-accent/10';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Good';
    if (score >= 70) return 'Fair';
    return 'Needs Improvement';
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
      <div className="flex items-center mb-4">
        <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center mr-3">
          <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-6a1 1 0 00-1-1H9a1 1 0 00-1 1v6a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-zinc-900">
          Business Information
        </h3>
      </div>
      
      <div className="space-y-4">
        {/* Business Name */}
        <div>
          <label className="block text-sm font-medium text-zinc-700 mb-1">
            Business Name
          </label>
          <p className="text-zinc-900 font-medium break-words">{businessInfo.name}</p>
        </div>

        {/* Address */}
        <div>
          <label className="block text-sm font-medium text-zinc-700 mb-1">
            Address
          </label>
          <p className="text-zinc-600 text-sm leading-relaxed break-words">
            {businessInfo.address}
          </p>
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-zinc-700 mb-1">
            Phone Number
          </label>
          <p className="text-zinc-900 font-medium">
            <a 
              href={`tel:${businessInfo.phone}`}
              className="hover:text-accent transition-colors inline-flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
              </svg>
              {businessInfo.phone}
            </a>
          </p>
        </div>

        {/* Total Score */}
        <div>
          <label className="block text-sm font-medium text-zinc-700 mb-2">
            Website Quality Score
          </label>
          <div className="flex items-center space-x-3 flex-wrap">
            <div className={`px-3 py-2 rounded-full text-sm font-semibold ${getScoreColor(businessInfo.totalScore)}`}>
              {businessInfo.totalScore}/100
            </div>
            <span className="text-sm text-zinc-600">
              {getScoreLabel(businessInfo.totalScore)}
            </span>
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            Based on design, content quality, and SEO optimization
          </p>
        </div>
      </div>

      {/* Features List */}
      <div className="mt-6 pt-6 border-t border-zinc-200">
        <h4 className="text-sm font-semibold text-zinc-900 mb-3 flex items-center">
          <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          What's Included
        </h4>
        <ul className="space-y-2 text-sm text-zinc-600">
          <li className="flex items-start">
            <svg className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Professional website design</span>
          </li>
          <li className="flex items-start">
            <svg className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Domain registration & hosting</span>
          </li>
          <li className="flex items-start">
            <svg className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Mobile-responsive design</span>
          </li>
          <li className="flex items-start">
            <svg className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>SEO optimization</span>
          </li>
          <li className="flex items-start">
            <svg className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Analytics & tracking</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default BusinessInfoSidebar;